import React, { useContext, useMemo, useState } from 'react';
import EDDialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import { BokforderWrapperContext } from '../../../../Rutiner/Bokforder/Contexts/BokforderWrapperContext';
import EDRadioGroup, {
  IRadioDef,
} from '../../../../../../../BaseComponents/RadioGroup/EDRadioGroup';
import { EDAutoCompleteEdit } from '../../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import { FranvaroContext } from '../../../../../SystemAdministration/ObjektVyer/User/Context/FranvaroContext/FranvaroContext';
import { useBusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { BokforderQuery_node_TEDEKBokforder } from '../../Queries/_QueryTypes/BokforderQuery';
import { BokforderType__Associations_Rader } from '../../Queries/_QueryTypes/BokforderType';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import { ActiveDatabaseContext } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { LoginStateContext } from '../../../../../../Login/Contexts/LoginStateContext/LoginStateContext';
import {
  IFranvaroUserOption,
  certifyOrRetakeRows,
  getRowsDeleteObjects,
  getAttestanterWithCurrentUserAsReserv,
} from '../../Utils/BokforderUtils';
import { mergeBusinessDataMutations } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/MergeBusinessDataMutations';
import {
  FormUpdateAssociatonType,
  getFormUpdateValue,
} from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';
import { StandardRutinContext } from '../../../../../_StandardRutin/StandardRutinContext';
import { AdditionalAttestDialogOpenEnum } from '../../../../Rutiner/Bokforder/Components/BokforderProviderWrapper';
import { capitalize } from '../../../../../../../Utils/stringUtils';
import {
  TEDEKBokforderSTATUSEnum,
  TEDEKBokforderTYPEnum,
} from '../../../../../../../globalQueryTypes';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';

interface IProps {
  type: TEDEKBokforderTYPEnum;
}

enum RegisteredValues {
  EJ_ANGIVET = 'EJ_ANGIVET',
  SAMTLIGA = 'SAMTLIGA',
  ENSKILD = 'ENSKILD',
}

const AdditionalAttestDialog: React.FC<IProps> = props => {
  const { userID } = useContext(ActiveDatabaseContext);
  const loginContext = useContext(LoginStateContext);
  const bokforderWrapperContext = useContext(BokforderWrapperContext);
  const franvaroContext = useContext(FranvaroContext);
  const standardRutinContext = useContext(StandardRutinContext);
  const formContext =
    useBusinessDataFormContext<BokforderQuery_node_TEDEKBokforder>();
  const setAdditionalAttestDialogOpen =
    bokforderWrapperContext.setAdditionalAttestDialogOpen;
  const [chosenRadioOption, setChosenRadioOption] = useState<RegisteredValues>(
    RegisteredValues.EJ_ANGIVET
  );
  const [selectedUser, setSelectedUser] = useState<IFranvaroUserOption | null>(
    null
  );

  //Kolla på currentValues, är den undefined (vid första renderingen kolla på data istället)
  const attestRows: BokforderType__Associations_Rader[] =
    formContext?.formState.currentValues?._Associations?.Rader?.filter(
      (row: any) => row?._Associations?.Attestant?.id === userID
    ) ??
    formContext?.data?._Associations?.Rader?.filter(
      (row: any) => row?._Associations?.Attestant?.id === userID
    ) ??
    [];

  const isAttestant = attestRows.length > 0;

  const currentAttestanterWithMeAsReserv: IFranvaroUserOption[] = useMemo(
    () =>
      getAttestanterWithCurrentUserAsReserv(
        formContext?.formState.currentValues?._Associations?.Rader ?? [],
        franvaroContext.franvaroReservForData,
        formContext?.data?.TYP ?? props.type
      ),
    [
      formContext?.data?.TYP,
      formContext?.formState.currentValues?._Associations?.Rader,
      franvaroContext.franvaroReservForData,
      props.type,
    ]
  );

  const filteredOptionsForAttest = currentAttestanterWithMeAsReserv.filter(
    attestant => !attestant.Attesterat
  );

  const filteredOptionsForRetake = currentAttestanterWithMeAsReserv.filter(
    attestant => attestant.Attesterat && attestant.AttesteratAsCurrentReserv
  );

  if (isAttestant && userID) {
    const currentUserOption: IFranvaroUserOption = {
      id: userID,
      Fullname: loginContext.name,
      SIGNATURE: loginContext.sign,
      Attesterat: attestRows.some(rows => rows.ATTESTERAD_DATUM != null),
      AttesteratAsCurrentReserv: false,
    };
    if (
      !filteredOptionsForAttest.some(user => user.id === currentUserOption.id)
    ) {
      filteredOptionsForAttest.push(currentUserOption);
    }
    if (
      currentUserOption.Attesterat &&
      !filteredOptionsForRetake.some(user => user.id === currentUserOption.id)
    ) {
      filteredOptionsForRetake.push(currentUserOption);
    }
  }

  const radioDefsFI: IRadioDef[] = [
    {
      label: 'Samtliga attestanter',
      value: RegisteredValues.SAMTLIGA,
    },
    {
      label: 'Enskild attestant',
      value: RegisteredValues.ENSKILD,
    },
  ];

  const attestOrRetakeString =
    bokforderWrapperContext.reservAttestDialogOpen ===
    AdditionalAttestDialogOpenEnum.ATERTA
      ? 'återta'
      : 'attestera';

  return (
    <EDDialog
      open={
        bokforderWrapperContext.reservAttestDialogOpen !=
        AdditionalAttestDialogOpenEnum.CLOSED
      }
      title="Välj attestant"
      fullWidth
      onExited={() => {
        setChosenRadioOption(RegisteredValues.EJ_ANGIVET);
        setSelectedUser(null);
      }}
      maxWidth={'xs'}
      footerActions={[
        {
          buttonProps: {
            disabled:
              formContext?.formState.loading || formContext?.mutationLoading,
          },
          label: 'Stäng',
          onClick: () => {
            setAdditionalAttestDialogOpen(
              AdditionalAttestDialogOpenEnum.CLOSED
            );
          },
        },
        {
          buttonProps: {
            disabled:
              chosenRadioOption === RegisteredValues.EJ_ANGIVET ||
              (chosenRadioOption === RegisteredValues.ENSKILD &&
                selectedUser == null) ||
              formContext?.formState.loading ||
              formContext?.mutationLoading,
          },
          label: capitalize(attestOrRetakeString),
          onClick: () => {
            const rows: BokforderType__Associations_Rader[] =
              formContext?.formState.currentValues?._Associations?.Rader ?? [];

            const isKontrollattestant =
              formContext?.formState.currentValues?._Associations
                ?.Kontrollattestant?.id === userID;

            let updateValues: FormUpdateAssociatonType[][] = [];

            //Ta bort tomma rader
            updateValues = updateValues.concat(
              getRowsDeleteObjects(
                formContext?.formState.currentValues,
                true,
                true
              )
            );

            const dateForAttest =
              bokforderWrapperContext.reservAttestDialogOpen ===
              AdditionalAttestDialogOpenEnum.ATERTA
                ? ''
                : new Date().toLocaleDateString('sv-SE');

            if (
              bokforderWrapperContext.reservAttestDialogOpen ===
              AdditionalAttestDialogOpenEnum.ATERTA
            ) {
              updateValues.push([
                {
                  operation: 'OwnFields',
                  values: {
                    STATUS: TEDEKBokforderSTATUSEnum.VANTAR_PA_ATTEST,
                    STATUS_DATUM: new Date().toLocaleDateString('sv-SE'),
                  },
                },
              ]);
            }

            if (isKontrollattestant) {
              updateValues.push([
                {
                  operation: 'OwnFields',
                  values: {
                    KONTROLLATTESTERAD_DATUM: dateForAttest,
                  },
                },
              ]);
            }

            if (
              chosenRadioOption === RegisteredValues.ENSKILD &&
              selectedUser
            ) {
              updateValues = updateValues.concat(
                certifyOrRetakeRows(
                  rows,
                  selectedUser.id ?? '',
                  dateForAttest,
                  selectedUser.id !== userID ? userID ?? '' : undefined
                )
              );
            }

            if (chosenRadioOption === RegisteredValues.SAMTLIGA) {
              const usersToAttestOrRetake =
                bokforderWrapperContext.reservAttestDialogOpen ===
                AdditionalAttestDialogOpenEnum.ATERTA
                  ? filteredOptionsForRetake
                  : filteredOptionsForAttest;

              usersToAttestOrRetake.forEach(attestant => {
                updateValues = updateValues.concat(
                  certifyOrRetakeRows(
                    rows,
                    attestant.id ?? '',
                    dateForAttest,
                    attestant.id !== userID ? userID ?? '' : undefined
                  )
                );
              });
            }

            formContext?.saveForm(
              mergeBusinessDataMutations(
                formContext?.formState?.values ?? {},
                getFormUpdateValue(updateValues, formContext?.data ?? {})
              ),
              () => {
                setAdditionalAttestDialogOpen(
                  AdditionalAttestDialogOpenEnum.CLOSED
                );
                if (
                  bokforderWrapperContext.reservAttestDialogOpen ===
                  AdditionalAttestDialogOpenEnum.ATTESTERA
                ) {
                  standardRutinContext.selectNextDetail.current?.(true);
                }
              }
            );
          },
        },
      ]}
      onClose={() => {
        setAdditionalAttestDialogOpen(AdditionalAttestDialogOpenEnum.CLOSED);
      }}
    >
      {formContext?.formState.loading ||
        (formContext?.mutationLoading && <EDAvenyProgress />)}
      <EDTypography>{`Du kan ${attestOrRetakeString} för mer än en attestant.`}</EDTypography>
      <EDRadioGroup
        fullWidth
        label={`Vem vill du ${attestOrRetakeString} för?`}
        marginTop="none"
        paddingLeft="none"
        radioDefs={radioDefsFI}
        value={chosenRadioOption}
        onChange={(newValue: string) => {
          const parsedValue = newValue as RegisteredValues;
          setChosenRadioOption(parsedValue);
          if (parsedValue === RegisteredValues.SAMTLIGA) {
            setSelectedUser(null);
          }
        }}
      />

      <EDAutoCompleteEdit
        disabled={chosenRadioOption !== RegisteredValues.ENSKILD}
        label="Attestant"
        fullWidth
        getOptionLabel={option =>
          option?.Fullname ?? 'Namnet kunde inte hittas'
        }
        onChange={option => setSelectedUser(option)}
        options={
          bokforderWrapperContext.reservAttestDialogOpen ===
          AdditionalAttestDialogOpenEnum.ATERTA
            ? filteredOptionsForRetake
            : filteredOptionsForAttest
        }
        value={selectedUser}
      />
    </EDDialog>
  );
};

export default AdditionalAttestDialog;
